/**
 * Partner Branding Context
 *
 * On boot, resolves the active partner retailer (if any) from:
 *   1. `?retailer=<slug>` query param — applies for this page load only.
 *      Removing the param from the URL on the next load reverts to the
 *      apex experience (no sessionStorage stickiness).
 *   2. The hostname — looking for `<slug>.estimator.trusscore.com` or
 *      `<slug>.beta.estimator.trusscore.com`.
 *
 * On a hit, fetches the public branding payload and applies it as CSS
 * custom properties on `<html>` BEFORE rendering children. The provider
 * renders a blank placeholder while the resolver is in flight so the
 * customer never sees the apex Trusscore palette flash before the
 * partner theme arrives. Apex / dev / non-partner cases skip the wait
 * entirely (no fetch, no placeholder).
 *
 * Also injects `<meta name="robots" content="noindex,nofollow">` for
 * partner subdomains so flagship portals don't compete with the apex
 * domain in search.
 *
 * On 404 / disabled / non-partner, the context resolves to `null` and
 * the app renders unchanged Trusscore defaults — no error surfaced.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { fetchPartnerBySubdomain, PartnerBranding } from '../services/partnerBrandingApi';
import type { RetailerTheme } from '../services/adminRetailersApi';
import { setTrackingContext, TrackingEnvironment } from '../utils/gtmTracking';

const APEX = 'estimator.trusscore.com';

/**
 * Hostnames where we never run subdomain detection. Local dev / IP-based
 * access lands here; the only way to test partner branding from these is
 * the `?retailer=<slug>` override.
 */
const DEV_HOSTS = new Set(['localhost', '127.0.0.1', 'chriscme']);

interface PartnerBrandingContextValue {
  /** Resolved partner branding, or null on the apex / dev / non-partner. */
  partner: PartnerBranding | null;
  /** True while the resolver is in flight on initial boot. */
  loading: boolean;
  /** True when the host matches `*.beta.estimator.trusscore.com`. */
  isBeta: boolean;
  /**
   * True when the slug came from the `?retailer=` override rather than the
   * hostname. Useful for showing a "preview mode" indicator in dev.
   */
  isOverride: boolean;
  /**
   * Apply a partner's branding by slug AFTER boot — e.g. a shared estimate
   * re-applying the branded portal it was created in. Pass null to revert any
   * manually-applied branding back to the apex default (e.g. when the next
   * estimate viewed is an unbranded Trusscore one). No-ops when a URL/host
   * partner already resolved at boot (that always wins) or when the requested
   * state is already in effect.
   */
  applyPartnerSlug: (slug: string | null) => void;
}

const PartnerBrandingContext = createContext<PartnerBrandingContextValue | undefined>(undefined);

/**
 * Pull `<slug>` from the hostname, recognizing both prod and beta layouts.
 * Returns the slug or null when on the apex / dev. Reserved labels (`beta`,
 * `www`) never match — they're treated as the apex.
 */
function parseHostname(hostname: string): { slug: string | null; isBeta: boolean } {
  const h = hostname.toLowerCase();
  if (DEV_HOSTS.has(h)) return { slug: null, isBeta: false };
  if (h === APEX) return { slug: null, isBeta: false };
  if (h === `beta.${APEX}`) return { slug: null, isBeta: true };

  // Beta partner: <slug>.beta.estimator.trusscore.com
  const betaMatch = h.match(/^([a-z0-9-]+)\.beta\.estimator\.trusscore\.com$/);
  if (betaMatch) return { slug: betaMatch[1], isBeta: true };

  // Prod partner: <slug>.estimator.trusscore.com (where slug !== "beta")
  const prodMatch = h.match(/^([a-z0-9-]+)\.estimator\.trusscore\.com$/);
  if (prodMatch && prodMatch[1] !== 'beta') return { slug: prodMatch[1], isBeta: false };

  return { slug: null, isBeta: false };
}

/**
 * Resolve the override slug from the current URL. Query-param-only — the
 * presence of `?retailer=<slug>` is the single source of truth, so simply
 * removing the param from the URL (or hard-reloading a page that doesn't
 * carry it) reverts to apex/host-based detection. No sessionStorage
 * stickiness — that previously caused branding to persist across reloads
 * even after the user removed the param.
 *
 * Within a single SPA navigation session the override persists naturally
 * because the provider isn't remounted; only a hard reload re-runs this.
 */
function resolveOverrideSlug(search: string): { slug: string | null; isOverride: boolean } {
  const params = new URLSearchParams(search);
  if (!params.has('retailer')) return { slug: null, isOverride: false };
  const raw = (params.get('retailer') ?? '').trim().toLowerCase();
  if (raw === '') return { slug: null, isOverride: false };
  return { slug: raw, isOverride: true };
}

/**
 * Map a RetailerTheme into the CSS custom property names that stylesheets
 * reference. Each property gets cleared (removed) when the value is null
 * so unset slots fall back to the in-CSS default.
 */
const THEME_VAR_MAP: Record<keyof RetailerTheme, string> = {
  // Nav
  navBg:                 '--partner-nav-bg',
  navFg:                 '--partner-nav-fg',
  navHoverBg:            '--partner-nav-hover-bg',
  // Brand
  primary:               '--partner-primary',
  accent:                '--partner-accent',
  // Primary buttons
  buttonBg:              '--partner-button-bg',
  buttonFg:              '--partner-button-fg',
  // Secondary buttons (slate)
  secondaryBg:           '--partner-secondary-bg',
  secondaryFg:           '--partner-secondary-fg',
  // Modal headers
  modalHeaderBg:         '--partner-modal-header-bg',
  modalHeaderFg:         '--partner-modal-header-fg',
  // Tables
  tableHeaderBg:         '--partner-table-header-bg',
  tableHeaderFg:         '--partner-table-header-fg',
  tableTotalBg:          '--partner-table-total-bg',
  tableTotalFg:          '--partner-table-total-fg',
  // Cost total card
  estimateBg:            '--partner-estimate-bg',
  estimateValueColor:    '--partner-estimate-value',
  estimateValueAltColor: '--partner-estimate-value-alt',
  // Footer
  footerBg:              '--partner-footer-bg',
  footerFg:              '--partner-footer-fg',
  footerMutedFg:         '--partner-footer-muted-fg',
};

/** CSS-variable object for isolated admin previews without theming the page root. */
export function retailerThemePreviewStyle(theme: RetailerTheme | null): React.CSSProperties {
  const style: Record<string, string> = {};
  if (theme) {
    (Object.keys(THEME_VAR_MAP) as Array<keyof RetailerTheme>).forEach(key => {
      const value = theme[key];
      if (value) style[THEME_VAR_MAP[key]] = value;
    });
  }
  return style as React.CSSProperties;
}

/**
 * Apply a theme as CSS custom properties on the document root. Returns a
 * cleanup that removes them — used so a theme swap (override change) or
 * unmount restores the apex defaults.
 */
function applyTheme(theme: RetailerTheme | null): () => void {
  const root = document.documentElement;
  const setKeys: string[] = [];
  if (theme) {
    (Object.keys(THEME_VAR_MAP) as Array<keyof RetailerTheme>).forEach((k) => {
      const value = theme[k];
      const cssVar = THEME_VAR_MAP[k];
      if (value && typeof value === 'string') {
        root.style.setProperty(cssVar, value);
        setKeys.push(cssVar);
      }
    });
  }
  return () => {
    setKeys.forEach((k) => root.style.removeProperty(k));
  };
}

/**
 * Inject (or remove) the partner-mode noindex meta tag. Idempotent: a
 * second call with the same flag is a no-op.
 */
function applyNoindex(enabled: boolean): void {
  const id = 'partner-noindex';
  const existing = document.head.querySelector(`meta#${id}`);
  if (enabled && !existing) {
    const meta = document.createElement('meta');
    meta.id = id;
    meta.name = 'robots';
    meta.content = 'noindex,nofollow';
    document.head.appendChild(meta);
  } else if (!enabled && existing) {
    existing.remove();
  }
}

export const PartnerBrandingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Hostname-based detection runs synchronously so the beta flag is
  // available on the first paint (banner shows without a flicker).
  const hostInfo = useMemo(() => parseHostname(window.location.hostname), []);
  const initialOverride = useMemo(() => resolveOverrideSlug(window.location.search), []);

  // The slug we fetch is override-first, hostname-second. Either may be
  // null (apex / dev / no override).
  const slug = initialOverride.slug ?? hostInfo.slug;
  const isOverride = initialOverride.isOverride;
  // isBeta from hostname is authoritative even when the override is set —
  // we want the beta banner to track the actual environment, not the
  // override target.
  const isBeta = hostInfo.isBeta;
  // Analytics environment, from the hostname pattern alone (no site list):
  // dev hosts never pollute production reports, beta is its own bucket.
  const environment: TrackingEnvironment = useMemo(() => {
    if (DEV_HOSTS.has(window.location.hostname.toLowerCase())) return 'dev';
    return isBeta ? 'beta' : 'production';
  }, [isBeta]);
  // Publish the resolved partner (or its absence) to the dataLayer context.
  // The slug/id are the retailer row's own admin values — never a code label.
  const publishTracking = useCallback((data: PartnerBranding | null, fallbackSlug: string | null) => {
    setTrackingContext({
      partnerSlug: data ? (data.subdomain || fallbackSlug || 'trusscore') : 'trusscore',
      partnerId: data?.id ?? null,
      environment,
    });
  }, [environment]);

  const [partner, setPartner] = useState<PartnerBranding | null>(null);
  const [loading, setLoading] = useState<boolean>(slug !== null);
  const cleanupRef = useRef<(() => void) | null>(null);

  // Boot fetch + theme application. Re-runs only if the slug changes
  // (e.g. user navigates with a new ?retailer= value).
  useEffect(() => {
    if (slug === null) {
      // No partner resolved — make sure any prior theme is cleared and
      // the noindex tag is gone (relevant when an override is cleared).
      cleanupRef.current?.();
      cleanupRef.current = null;
      applyNoindex(false);
      setPartner(null);
      setLoading(false);
      publishTracking(null, null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    fetchPartnerBySubdomain(slug)
      .then((data) => {
        if (cancelled) return;
        if (!data) {
          // 404 — unknown / disabled / non-partner. Fall back silently.
          setPartner(null);
          applyNoindex(false);
          publishTracking(null, null);
          return;
        }
        setPartner(data);
        cleanupRef.current?.();
        cleanupRef.current = applyTheme(data.theme);
        applyNoindex(true);
        publishTracking(data, slug);
      })
      .catch((err) => {
        // Network failure — log and fall back. Don't break the app.
        console.warn('[PartnerBranding] Resolver failed:', err);
        if (!cancelled) {
          setPartner(null);
          applyNoindex(false);
          publishTracking(null, null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug, publishTracking]);

  // Best-effort cleanup on unmount (the provider lives at app root, so
  // this only fires on full app teardown — but defensive anyway).
  useEffect(() => {
    return () => {
      cleanupRef.current?.();
      applyNoindex(false);
    };
  }, []);

  // Post-boot branding application (shared-estimate "branding follows the data").
  const appliedManualRef = useRef<string | null>(null);
  const applyPartnerSlug = useCallback((target: string | null) => {
    // A URL/host partner resolved at boot always wins — never override or clear it.
    if (slug !== null) return;
    // Already in the requested state (same slug applied, or already cleared).
    if (appliedManualRef.current === target) return;
    appliedManualRef.current = target;

    // Reverting to apex: strip the previously-applied partner theme so an
    // unbranded estimate viewed after a branded one shows Trusscore defaults.
    if (!target) {
      cleanupRef.current?.();
      cleanupRef.current = null;
      applyNoindex(false);
      setPartner(null);
      publishTracking(null, null);
      return;
    }

    fetchPartnerBySubdomain(target)
      .then((data) => {
        // Guard against a stale fetch resolving after a newer apply/clear.
        if (appliedManualRef.current !== target || !data) return;
        setPartner(data);
        cleanupRef.current?.();
        cleanupRef.current = applyTheme(data.theme);
        applyNoindex(true);
        publishTracking(data, target);
      })
      .catch((err) => console.warn('[PartnerBranding] applyPartnerSlug failed:', err));
  }, [slug, publishTracking]);

  const value = useMemo<PartnerBrandingContextValue>(
    () => ({ partner, loading, isBeta, isOverride, applyPartnerSlug }),
    [partner, loading, isBeta, isOverride, applyPartnerSlug],
  );

  // Gate the rest of the app behind partner resolution to eliminate the
  // apex-then-partner color flash. Apex / dev visits have slug === null,
  // loading flips to false synchronously in the boot effect, and children
  // render immediately. Partner visits hold for the resolver fetch
  // (cached 60s) so the first paint already carries the partner palette.
  const showPlaceholder = slug !== null && loading;

  return (
    <PartnerBrandingContext.Provider value={value}>
      {showPlaceholder ? <PartnerBootPlaceholder /> : children}
    </PartnerBrandingContext.Provider>
  );
};

/**
 * Minimal blank placeholder shown while the partner resolver is in
 * flight. Background is a neutral light gray so the page isn't a flash
 * of pure white — but no Trusscore branding leaks through. Anything more
 * elaborate (spinner, partner logo) would require knowing the partner
 * before resolution, defeating the purpose.
 */
const PartnerBootPlaceholder: React.FC = () => (
  <div
    aria-hidden
    style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: '#f8f9fa',
      zIndex: 99999,
    }}
  />
);

export function usePartnerBranding(): PartnerBrandingContextValue {
  const ctx = useContext(PartnerBrandingContext);
  if (!ctx) {
    throw new Error('usePartnerBranding must be used inside <PartnerBrandingProvider>');
  }
  return ctx;
}
